import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  api,
  ContactDto,
  CreateTaskCommentDto,
  CreateTaskDto,
  InferSearchParamsQueryTypeWithArraySorter,
  LogTimeDto,
  IpOrganizationTaskListDto,
  PlainExternalUserDto,
  TagDto,
  TagType,
  TaskCommentDto,
  TaskCommentListDto,
  TaskDto,
  TaskListDto,
  TaskTagDto,
  TaskVersionChangelogList,
  UpdateTaskDto,
} from '@sib/api';
import { CACHE_TIME, DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import { handleOptimisticUpdateTask } from './task.utils';

export const getInfiniteTaskListQueryKey = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.task.taskControllerFindAll
  >
) => [
  QueryKey.Resource.Task,
  QueryKey.Operation.List,
  payload,
  QueryKey.Operation.Infinite,
];

export const useInfiniteTaskList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.task.taskControllerFindAll
  >,
  options?: Partial<
    UseInfiniteQueryOptions<
      TaskListDto,
      Error,
      InfiniteData<TaskListDto>,
      TaskListDto,
      unknown[],
      number
    > & {
      shouldKeepPreviousData?: boolean;
    }
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: getInfiniteTaskListQueryKey(payload),

    queryFn: async ({ pageParam }) => {
      const organizationFilter = payload.filter?.find(i =>
        i.includes('organizationId')
      );

      if (organizationFilter) {
        const id = organizationFilter.split(':')[2];

        const data = await api.organization.organizationControllerFindAllTasks(
          id,
          {
            ...payload,
            page: pageParam,
          }
        );

        return { ...data, newItems: 0 };
      }

      const data = await api.task.taskControllerFindAll({
        ...payload,
        page: pageParam,
      });

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    ...(options?.shouldKeepPreviousData && {
      placeholderData: keepPreviousData,
    }),
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export const useTaskList = (
  payload: Parameters<typeof api.task.taskControllerFindAll>[0],
  options?: Partial<UseQueryOptions<TaskListDto, Error, TaskListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List, payload],
    queryFn: async () => api.task.taskControllerFindAll(payload),
  });

type ApiTasksCsvExportQuery = Parameters<
  typeof api.task.taskControllerExportTasks
>[0];

export type TasksCsvExportQuery = Omit<ApiTasksCsvExportQuery, 'columns'> & {
  columns: string[];
};

export const exportTasksCsv = async (query: TasksCsvExportQuery) =>
  api.task.taskControllerExportTasks(query, {
    format: 'blob',
  }) as Promise<Blob>;

export const useExportTasksCsv = (
  options?: Partial<UseMutationOptions<Blob, Error, TasksCsvExportQuery>>
) =>
  useMutation({
    ...options,
    mutationFn: exportTasksCsv,
  });

const GLOBAL_TASK_LIST_KEY = 'global';

/**
 * Lists tasks across all users. Unlike {@link useTaskList}, the backend does
 * not limit results to the current user.
 */
export const useGlobalTaskList = (
  payload: Parameters<
    typeof api.internalPortal.taskRelationsControllerGetTasks
  >[0],
  options?: Partial<UseQueryOptions<TaskListDto, Error, TaskListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Task,
      QueryKey.Operation.List,
      GLOBAL_TASK_LIST_KEY,
      payload,
    ],
    queryFn: async () =>
      api.internalPortal.taskRelationsControllerGetTasks(payload),
  });

export const exportGlobalTasksCsv = async (query: TasksCsvExportQuery) =>
  api.internalPortal.taskRelationsControllerExportTasks(query, {
    format: 'blob',
  }) as Promise<Blob>;

export const useExportGlobalTasksCsv = (
  options?: Partial<UseMutationOptions<Blob, Error, TasksCsvExportQuery>>
) =>
  useMutation({
    ...options,
    mutationFn: exportGlobalTasksCsv,
  });

export const useOrganizationTaskList = (
  {
    organizationId,
    payload,
  }: {
    organizationId: string;
    payload: Parameters<
      typeof api.organization.organizationControllerFindAllTasks
    >[1];
  },
  options?: UseQueryOptions<TaskListDto, Error, TaskListDto>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Task,
      QueryKey.Operation.List,
      payload,
      organizationId,
    ],
    queryFn: async () =>
      api.organization.organizationControllerFindAllTasks(
        organizationId,
        payload
      ),
  });

interface UseInternalPortalOrganizationTasksListPayload {
  organizationId: string;
  payload: Parameters<
    typeof api.internalPortal.organizationRelationsControllerGetOrganizationTasks
  >[1];
}

export const useInternalPortalOrganizationTasksList = (
  { organizationId, payload }: UseInternalPortalOrganizationTasksListPayload,
  options?: Partial<
    UseQueryOptions<IpOrganizationTaskListDto, Error, IpOrganizationTaskListDto>
  >
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Task,
      QueryKey.Operation.List,
      payload,
      organizationId,
      QueryKey.Resource.OrganizationTasks,
    ],
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetOrganizationTasks(
        organizationId,
        payload
      ),
  });

type ApiOrganizationTasksCsvExportQuery = Parameters<
  typeof api.internalPortal.organizationRelationsControllerExportOrganizationTasks
>[1];

export type OrganizationTasksCsvExportQuery = Omit<
  ApiOrganizationTasksCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportOrganizationTasksCsv = async (
  organizationId: string,
  query: OrganizationTasksCsvExportQuery
) =>
  api.internalPortal.organizationRelationsControllerExportOrganizationTasks(
    organizationId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportOrganizationTasksCsvOptions = UseMutationOptions<
  Blob,
  Error,
  OrganizationTasksCsvExportQuery
> & {
  organizationId: string;
};

export const useExportOrganizationTasksCsv = ({
  organizationId,
  ...options
}: UseExportOrganizationTasksCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables =>
      exportOrganizationTasksCsv(organizationId, variables),
  });

export const useProjectsTaskList = (
  {
    projectId,
    payload,
  }: {
    projectId: string;
    payload: Parameters<
      typeof api.internalPortal.projectRelationsControllerGetProjectTasks
    >[1];
  },
  options?: Partial<UseQueryOptions<TaskListDto, Error, TaskListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Task,
      QueryKey.Operation.List,
      payload,
      projectId,
    ],
    queryFn: async () =>
      api.internalPortal.projectRelationsControllerGetProjectTasks(
        projectId,
        payload
      ),
  });

type ApiProjectTasksCsvExportQuery = Parameters<
  typeof api.internalPortal.projectRelationsControllerExportProjectTasks
>[1];

export type ProjectTasksCsvExportQuery = Omit<
  ApiProjectTasksCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportProjectTasksCsv = async (
  projectId: string,
  query: ProjectTasksCsvExportQuery
) =>
  api.internalPortal.projectRelationsControllerExportProjectTasks(
    projectId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportProjectTasksCsvOptions = UseMutationOptions<
  Blob,
  Error,
  ProjectTasksCsvExportQuery
> & {
  projectId: string;
};

export const useExportProjectTasksCsv = ({
  projectId,
  ...options
}: UseExportProjectTasksCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables => exportProjectTasksCsv(projectId, variables),
  });

interface UseVendorTasksListPayload {
  vendorId: string;
  payload: Parameters<
    typeof api.internalPortal.vendorRelationsControllerGetVendorTasks
  >[1];
}

export const useVendorTasksList = (
  { vendorId, payload }: UseVendorTasksListPayload,
  options?: Partial<UseQueryOptions<TaskListDto, Error, TaskListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Task,
      QueryKey.Operation.List,
      payload,
      vendorId,
    ],
    queryFn: async () =>
      api.internalPortal.vendorRelationsControllerGetVendorTasks(
        vendorId,
        payload
      ),
  });

type ApiVendorTasksCsvExportQuery = Parameters<
  typeof api.internalPortal.vendorRelationsControllerExportVendorTasks
>[1];

export type VendorTasksCsvExportQuery = Omit<
  ApiVendorTasksCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportVendorTasksCsv = async (
  vendorId: string,
  query: VendorTasksCsvExportQuery
) =>
  api.internalPortal.vendorRelationsControllerExportVendorTasks(
    vendorId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportVendorTasksCsvOptions = UseMutationOptions<
  Blob,
  Error,
  VendorTasksCsvExportQuery
> & {
  vendorId: string;
};

export const useExportVendorTasksCsv = ({
  vendorId,
  ...options
}: UseExportVendorTasksCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables => exportVendorTasksCsv(vendorId, variables),
  });

export const useTaskCreate = (
  options?: UseMutationOptions<TaskDto, Error, CreateTaskDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      if (data?.organization?.id)
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.OrganizationMembers,
            data?.organization?.id,
            QueryKey.Operation.List,
          ],
        });

      if (data?.parentTask?.uuid)
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Task, data.parentTask?.uuid],
        });

      if (variables?.subauditId)
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.SubAuditHistory,
            variables.organizationId,
            variables.subauditId,
            QueryKey.Operation.List,
            QueryKey.Resource.Changelogs,
          ],
        });

      if (data?.vendor?.id) {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.One,
            data.vendor?.id,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
          ],
        });
      }

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationActiveTasks,
          QueryKey.Operation.List,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.TaskCount],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.Members],
      });

      queryClient.invalidateQueries({
        predicate: query => {
          const isArray = Array.isArray(query.queryKey);
          if (!isArray) return false;
          const isTeamQuery =
            query.queryKey.includes(QueryKey.Resource.Teams) &&
            query.queryKey.includes(QueryKey.Resource.TeamsSingle);

          const isTeamListQuery =
            query.queryKey.includes(QueryKey.Resource.Teams) &&
            (query.queryKey.includes(QueryKey.Operation.Infinite) ||
              query.queryKey.includes(QueryKey.Operation.List));

          return isTeamQuery || isTeamListQuery;
        },
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.AssignedTasks,
          QueryKey.Resource.TaskCount,
        ],
      });

      const relatedTicketId = data?.ticketId ?? variables?.ticketId;
      if (relatedTicketId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.TicketRelatedTasks, relatedTicketId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.Tickets,
            relatedTicketId,
            QueryKey.Operation.Count,
          ],
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: (payload: CreateTaskDto) =>
      api.task.taskControllerCreate(payload),
  });
};

export const useTaskUpdate = (
  options?: UseMutationOptions<TaskDto, Error, UpdateTaskDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, data.uuid],
      });
      if (data.parentTask?.uuid)
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Task, data.parentTask?.uuid],
        });

      if (data?.vendor?.id) {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.One,
            data.vendor?.id,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
          ],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationMembers],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.TeamsSingle],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.TaskCount],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskHistory,
          data.uuid,
          QueryKey.Resource.Changelogs,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.Members],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.AssignedTasks,
          QueryKey.Resource.TaskCount,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationActiveTasks,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ uuid, ...payload }: UpdateTaskDto & { uuid: string }) =>
      api.task.taskControllerUpdate(uuid, payload),
  });
};
export const useTaskDelete = (
  options?: UseMutationOptions<
    unknown,
    Error,
    { uuid: string; cascadeFollowUps?: boolean }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationUsers],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationMembers],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.TaskCount],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.Members],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.AssignedTasks,
          QueryKey.Resource.TaskCount,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.OrganizationActiveTasks,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(...args);
    },
    mutationFn: ({
      uuid,
      cascadeFollowUps,
    }: {
      uuid: string;
      cascadeFollowUps?: boolean;
    }) => api.task.taskControllerDeleteOne(uuid, { cascadeFollowUps }),
  });
};

export const useTaskOne = (
  uuid?: string | null,
  options?: UseQueryOptions<TaskDto | null, Error, TaskDto>
) =>
  useQuery({
    ...options,
    enabled: !!uuid,
    queryKey: [QueryKey.Resource.Task, uuid],
    queryFn: () => (uuid ? api.task.taskControllerFindOne(uuid) : null),
  });

export const useTaskCreateMany = (
  options?: UseMutationOptions<TaskDto[], Error, CreateTaskDto[]>
) =>
  useMutation({
    ...options,

    mutationFn: (payload: CreateTaskDto[]) =>
      api.task.taskControllerCreateManyTasks(payload),
  });

export const useTaskUpdateMany = (
  options?: UseMutationOptions<
    TaskDto[],
    Error,
    UpdateTaskDto & { ids: string[] }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.TaskCount],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams, QueryKey.Resource.Members],
      });

      if (variables.assignee)
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.OrganizationMembers],
        });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.AssignedTasks,
          QueryKey.Resource.TaskCount,
        ],
      });

      data.forEach(task => {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Task, task.uuid],
        });
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ ids, ...payload }: UpdateTaskDto & { ids: string[] }) => {
      const requests = ids.map(id =>
        api.task.taskControllerUpdate(id, payload)
      );

      return Promise.all(requests);
    },
  });
};

export const useTaskUpdateOptimistic = (
  options?: UseMutationOptions<TaskDto, Error, UpdateTaskDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData(
        {
          queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
        },
        (data: InfiniteData<TaskListDto>) => {
          const { tags, title, category, ...updateVariables } = variables;

          return handleOptimisticUpdateTask(
            {
              ...updateVariables,
              ...(title && { title }),
              ...(category && { category }),
              ...(variables.seen && { seenAt: new Date().toDateString() }),
              ...(variables.completed && {
                completedAt: new Date().toDateString(),
              }),
              ...(tags && {
                tags: tags.map(t => ({
                  type: t.type,
                  ...(t.type === TagType.Custom && {
                    tag: { uuid: t.id } as TagDto,
                  }),
                  ...(t.type === TagType.Contact && {
                    contact: { id: t.id } as ContactDto,
                  }),
                  ...(t.type === TagType.Employee && {
                    employee: { id: t.id } as PlainExternalUserDto,
                  }),
                })) as TaskTagDto[],
              }),
            },
            data
          );
        }
      );

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ uuid, ...payload }: UpdateTaskDto & { uuid: string }) =>
      api.task.taskControllerUpdate(uuid, payload),
  });
};

export const useTaskChangeLog = (
  id: string,
  options?: UseQueryOptions<
    TaskVersionChangelogList | null,
    Error,
    TaskVersionChangelogList
  >
) =>
  useQuery({
    ...options,
    enabled: !!id,
    queryKey: [QueryKey.Resource.TaskHistory, id, QueryKey.Resource.Changelogs],
    queryFn: () =>
      id ? api.tasks.taskHistoryControllerFindAllChangeLogs(id) : null,
  });

export const useTaskLogTime = (
  options?: UseMutationOptions<TaskDto, Error, LogTimeDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, QueryKey.Operation.List],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, data.uuid],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ uuid, ...payload }: LogTimeDto & { uuid: string }) =>
      api.task.taskControllerLogTime(uuid, payload),
  });
};

export const useInfiniteTaskComments = (
  taskId: string,
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tasks.taskCommentControllerFindAll,
    1
  >,
  options?: Partial<
    UseInfiniteQueryOptions<
      TaskCommentListDto,
      Error,
      InfiniteData<TaskCommentListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.TaskComments,
      taskId,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],

    queryFn: async ({ pageParam }) => {
      const data = await api.tasks.taskCommentControllerFindAll(taskId, {
        ...payload,
        page: pageParam as number,
      });

      return data;
    },

    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export const useCreateTaskComment = (
  taskId: string,
  options?: UseMutationOptions<TaskCommentDto, Error, CreateTaskCommentDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      await api.tasks.taskCommentControllerCreate(taskId, data),

    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskComments,
          taskId,
          QueryKey.Operation.List,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Task, taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TaskHistory,
          taskId,
          QueryKey.Resource.Changelogs,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
